import React from 'react';
import { jwtDecode } from 'jwt-decode';
import { push } from 'redux-first-history';
import type { IntlMessage } from 'modules/notifications/types/Notification';

import api from 'service';
import { br } from 'utils/i18nUtils';

import {
  DecodedAdminToken,
  GetMFAConnectionResponse,
  LoginMFAResponse,
  LoginResponse,
  SendAdminCodeResponse,
  SendMFACodeResponse,
} from 'modules/auth/types/LoginResponse';
import {
  ChannelEnum,
  CheckQRCodeTokenRequest,
  GetMFAConnectionRequest,
  LoginMFARequest,
  LoginRequest,
  SendAdminCodeRequest,
  SendMFACodeRequest,
  SendQrCodeRequest,
} from 'modules/auth/types/LoginRequest';
import type { RootState } from 'App/Store';
import { Token } from 'modules/auth/types/Token';
import { InitialState } from 'modules/auth/types/InitialState';
import { ErrorTypeEnum, DEALER_ERROR_TYPES } from 'types/Error';
import { SelfServiceType } from 'modules/selfServices/types/SelfService';
import { ComponentType, NotificationType } from 'modules/notifications/types/Notification';

import {
  ADMIN, KIOSK, LOGIN, LOGIN_MFA,
  QR_CODE_LOGIN_PATH,
} from 'constants/url';

import { addNotification } from 'modules/notifications/actions';

import { isBreakdownAvailable } from 'modules/dealers/selectors';
import { getSearch, isAdminLoginPage as isAdminLoginPageSelector } from 'modules/router/selectors';

import { getNotFoundSelfService } from './selectors';

interface ErrorData {
  type?: ErrorTypeEnum;
  status: string;
  message: string;
  data?: Record<string, React.ReactNode>;
}

const createErrorNotification = (
  {
    type, status, message, data,
  }: ErrorData,
  title: IntlMessage = {
    id: 'login.error.title',
    defaultMessage: 'Unable to login',
  },
) => addNotification({
  title,
  description: {
    id: type ? `error.types.${type}` : `login.error.description.${status}`,
    defaultMessage: message ?? 'Unknown error',
    values: { br, ...data },
  },
  type: NotificationType.ERROR,
  componentType: DEALER_ERROR_TYPES.includes(type) ? ComponentType.MODAL : ComponentType.ALERT,
});

const authApi = api.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<InitialState, LoginRequest>({
      query: ({ registration, locationId }) => ({
        method: 'POST',
        url: LOGIN,
        body: { registration, locationId },
      }),
      transformResponse: (response: LoginResponse) => {
        const token = jwtDecode<Token>(response.token);
        return {
          ...response, selfServiceId: token.self_service_id, dealerId: token.dealer_id,
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch ({ error }) {
          dispatch(
            createErrorNotification({
              type: error?.data?.errorType,
              message: error?.data?.errorMessage,
              status: error?.status,
            }),
          );
        }
      },
    }),
    getMFAConnection: builder.mutation<GetMFAConnectionResponse, GetMFAConnectionRequest>({
      query: ({ type = SelfServiceType.CHECK_IN, ...params }) => ({
        method: 'GET',
        url: LOGIN_MFA,
        params: { ...params, type },
      }),
      async onQueryStarted({ type }, { dispatch, queryFulfilled, getState }) {
        try {
          await queryFulfilled;
        } catch ({ error }) {
          if (error?.data?.errorType !== ErrorTypeEnum.SO_WRONG_KIOSK) {
            let hideNotification = false;

            if (type === SelfServiceType.CHECK_IN && error?.status === 404) {
              const state = getState() as RootState;

              const breakdownAvailable = isBreakdownAvailable(state);
              const isNotFoundSelfService = getNotFoundSelfService(state);

              hideNotification = breakdownAvailable && isNotFoundSelfService;
            }

            if (!hideNotification) {
              dispatch(
                createErrorNotification({
                  type: error?.data?.errorType,
                  message: error?.data?.errorMessage,
                  status: error?.status,
                  data: error?.data?.errorMetadata,
                }),
              );
            }
          }
        }
      },
      transformResponse: (response: GetMFAConnectionResponse) => {
        if (Array.isArray(response.kioskDealers)) {
          const kioskDealersWithSortedBrands = response.kioskDealers.map((dealer) => ({
            ...dealer,
            brands: dealer.brands.sort(({ name: aName }, { name: bName }) => aName.localeCompare(bName)),
          }));
          const sortedBrands = response.brands.sort(({ name: aName }, { name: bName }) => aName.localeCompare(bName));
          return { ...response, kioskDealers: kioskDealersWithSortedBrands, brands: sortedBrands };
        }
        return response;
      },
    }),
    sendQrCode: builder.mutation<InitialState, SendQrCodeRequest>({
      query: ({ qrCode, type }) => ({
        method: 'POST',
        url: QR_CODE_LOGIN_PATH,
        body: { code: qrCode, type },
      }),
      transformResponse: (response: LoginResponse) => {
        const decodedToken = jwtDecode<Token>(response.token);
        return {
          ...response,
          selfServiceId: decodedToken.self_service_id,
          dealerId: decodedToken.dealer_id,
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch ({ error }) {
          const hasCodeMetadata = error?.data?.errorMetadata?.remainingCode !== undefined;
          if (!hasCodeMetadata) {
            dispatch(createErrorNotification({
              type: error?.data?.errorType,
              message: error?.data?.errorMessage,
              status: error?.status,
            }));
          }
        }
      },
    }),
    sendMFACode: builder.mutation<SendMFACodeResponse, SendMFACodeRequest>({
      query: ({ connectionId, selfServiceId, channel = ChannelEnum.SMS }) => ({
        method: 'POST',
        url: `${LOGIN_MFA}/send`,
        body: { connectionId, selfServiceId, channel },
      }),
      transformResponse: (response: SendMFACodeResponse, _, arg: SendMFACodeRequest) => ({
        ...response,
        channel: arg.channel,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch ({ error }) {
          dispatch(
            createErrorNotification(
              {
                type: error?.data?.errorType,
                message: error?.data?.errorMessage,
                status: error?.status,
              },
              { id: 'login.error.sendTitle', defaultMessage: 'Send failed' },
            ),
          );
        }
      },
    }),
    loginMFA: builder.mutation<LoginMFAResponse, LoginMFARequest>({
      query: ({
        selfServiceId, connectionId, code, breakdownServiceAuthorType,
      }) => ({
        method: 'POST',
        url: `${LOGIN_MFA}/login`,
        body: {
          code,
          connectionId,
          selfServiceId,
          ...(breakdownServiceAuthorType ? { context: { breakdownServiceAuthorType } } : {}),
        },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch ({ error }) {
          if (error?.data?.errorType !== ErrorTypeEnum.MFA_WRONG_CODE) {
            dispatch(
              createErrorNotification({
                type: error?.data?.errorType,
                message: error?.data?.errorMessage,
                status: error?.status,
              }),
            );
          }
        }
      },
    }),
    checkQRCodeToken: builder.mutation<void, CheckQRCodeTokenRequest>({
      query: ({ qrCodeToken, kioskId }) => ({
        method: 'POST',
        url: `${ADMIN}${KIOSK}/${kioskId}/login`,
        headers: { Authorization: `${qrCodeToken}` },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled, getState }) {
        try {
          const { meta } = await queryFulfilled;

          if (meta.response.status === 204) {
            const search = getSearch(getState() as RootState);
            dispatch(push(`/admin/login${search}`));
          }
        } catch ({ error }) {
          dispatch(createErrorNotification({
            status: error?.status,
            type: ErrorTypeEnum.SO_WRONG_QR_CODE,
            // eslint-disable-next-line max-len
            message: 'The scanned QR code is not valid or has expired.{br}Please ensure that you have a working internet connection on your mobile device.',
          }));
        }
      },
    }),
    loginAdmin: builder.mutation<SendAdminCodeResponse & DecodedAdminToken, SendAdminCodeRequest>({
      query: ({ qrCodeToken, kioskId, pinCode }) => ({
        method: 'POST',
        url: `${ADMIN}${KIOSK}/${kioskId}/login`,
        body: { pinCode },
        headers: { Authorization: qrCodeToken },
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled, getState }) => {
        try {
          await queryFulfilled;

          const state = getState() as RootState;
          const search = getSearch(state);
          const isLoginPage = isAdminLoginPageSelector(state);

          if (isLoginPage) {
            dispatch(push(`/admin${search}`));
          }
        } catch {
          //
        }
      },
      transformResponse: (response: SendAdminCodeResponse) => {
        const decodedToken = jwtDecode<DecodedAdminToken>(response.token);
        return { ...response, ...decodedToken };
      },
    }),
  }),
});

export default authApi;

export const {
  useLoginMutation,
  useGetMFAConnectionMutation,
  useSendMFACodeMutation,
  useLoginMFAMutation,
  useCheckQRCodeTokenMutation,
  useLoginAdminMutation,
} = authApi;
